import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight, Sparkles, Gem, Tag, ShieldCheck } from "lucide-react";
import { RevealLines } from "@/components/Reveal";
import { scrollToId } from "@/lib/api";

const ease = [0.22, 1, 0.36, 1];

const ArtCard = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div style={{ perspective: 1200 }} onMouseMove={onMove} onMouseLeave={reset} className="relative">
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        initial={{ clipPath: "inset(100% 0 0 0 round 200px 200px 24px 24px)" }}
        animate={{ clipPath: "inset(0% 0 0 0 round 200px 200px 24px 24px)" }}
        transition={{ duration: 1.6, delay: 0.5, ease }}
        className="relative aspect-[3/4.3] w-full overflow-hidden shadow-[0_40px_120px_-20px_rgba(192,132,252,0.45)]"
        data-testid="hero-art-card">
        <motion.img src="/img/hero-art.webp" alt="Two figures drifting among lavender blossoms under a glowing moon"
          initial={{ scale: 1.35 }} animate={{ scale: 1.08 }} transition={{ duration: 2.4, delay: 0.5, ease }}
          className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0e0616]/70 via-transparent to-transparent" />
        <div style={{ transform: "translateZ(60px)" }} className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
          <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-purple-100">Chapter I — The Moon Bridge</span>
          <Sparkles className="w-4 h-4 text-amber-200" />
        </div>
      </motion.div>
      <div className="absolute -inset-6 -z-10 rounded-full bg-purple-500/20 blur-3xl" />
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yArt = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const yText = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);
  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen pt-28 md:pt-32 pb-16 overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: yBg }} className="absolute inset-0">
        <motion.img src="/img/sky-b.jpg" alt="" initial={{ scale: 1.2, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 2.4, ease }} className="w-full h-full object-cover" data-testid="hero-bg" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#0e0616]/85 via-[#0e0616]/55 to-[#0e0616]/25" />
      <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-[#0e0616] to-transparent" />
      <div className="absolute inset-0 pointer-events-none">
        {Array.from({ length: 40 }).map((_, i) => (
          <span key={i} className="absolute w-[2px] h-[2px] rounded-full bg-purple-100"
            style={{ left: `${(i * 37) % 100}%`, top: `${(i * 53) % 100}%`, animation: `twinkle ${3 + (i % 5)}s ease-in-out ${i * 0.2}s infinite` }} />
        ))}
      </div>
      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <motion.div style={{ y: yText }} className="lg:col-span-7 relative z-10">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}
            className="font-mono text-[11px] uppercase tracking-[0.28em] text-purple-200 mb-8 flex items-center gap-3">
            <span className="w-10 h-px bg-purple-200" /> Handmade bracelets · Shared thoughts
          </motion.p>
          <h1 className="font-display font-normal tracking-tight leading-[0.86] text-[15vw] sm:text-7xl lg:text-[6.6rem]" data-testid="hero-title">
            <RevealLines delay={0.3} lines={[
              <>Whisper</>,
              <>your <em className="text-purple-300">story</em></>,
              <>to the <em className="text-amber-200">moon</em>.</>,
            ]} />
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 1, ease }}
            className="mt-10 max-w-md text-base md:text-lg text-purple-100/85 leading-relaxed">
            A lavender hollow where strangers leave their thoughts like pressed flowers — and where every bracelet is strung to keep one.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 1, ease }}
            className="mt-10 flex flex-wrap gap-4">
            <button data-testid="hero-shop-btn" onClick={() => scrollToId("shop")}
              className="group inline-flex items-center gap-3 rounded-full bg-purple-200 text-[#0e0616] pl-7 pr-2 py-2 font-medium hover:bg-amber-200 transition-colors duration-500">
              Find your bracelet
              <span className="w-10 h-10 rounded-full bg-[#0e0616] text-purple-100 grid place-items-center transition-transform duration-500 group-hover:rotate-[-45deg]">
                <ArrowDownRight className="w-4 h-4" />
              </span>
            </button>
            <button data-testid="hero-thoughts-btn" onClick={() => scrollToId("thoughts")}
              className="rounded-full border border-purple-300/40 px-7 py-4 hover:border-amber-200 hover:text-amber-200 transition-colors duration-500">
              Read the thoughts
            </button>
          </motion.div>
          <motion.ul initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6, duration: 1 }}
            className="mt-12 flex flex-wrap gap-x-8 gap-y-3 text-sm text-purple-100/90" data-testid="hero-trust-row">
            {[[Gem, "Handmade by Liza"], [Tag, "Bracelets from $5"], [ShieldCheck, "Secure Stripe checkout"]].map(([Icon, label]) => (
              <li key={label} className="flex items-center gap-2"><Icon className="w-4 h-4 text-amber-200" />{label}</li>
            ))}
          </motion.ul>
        </motion.div>
        <motion.div style={{ y: yArt }} className="lg:col-span-5 relative max-w-md mx-auto w-full">
          <ArtCard />
        </motion.div>
      </div>
    </section>
  );
};
